import type { EmblaCarouselType } from 'embla-carousel'
import useEmblaCarousel from 'embla-carousel-react'
import { useCallback, useEffect, useRef } from 'react'

import { usePrevNextButtons } from './usePrevNextButtons'

export const useUpgradeCarousel = (
  onSetActiveSlide: (index: number) => void,
  activeIndex: number
) => {
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true })
  const lastSyncedRef = useRef<number | null>(null)

  const normalizeIndex = useCallback((api: EmblaCarouselType, i: number) => {
    const snaps = api.scrollSnapList()
    const len = snaps.length
    if (len === 0) {
      return 0
    }

    return ((i % len) + len) % len
  }, [])

  const handleSelect = useCallback((api: EmblaCarouselType) => {
    const i = api.selectedScrollSnap()
    onSetActiveSlide(i)
  }, [])

  useEffect(() => {
    if (!emblaApi) {
      return
    }

    const syncToExternal = () => {
      const target = normalizeIndex(emblaApi, activeIndex)

      const current = emblaApi.selectedScrollSnap()
      if (current === target && lastSyncedRef.current === target) {
        return
      }

      emblaApi.scrollTo(target, false)
      lastSyncedRef.current = target
    }

    syncToExternal()

    emblaApi.on('reInit', syncToExternal)
    emblaApi.on('select', handleSelect)

    return () => {
      emblaApi.off('reInit', syncToExternal)
      emblaApi.off('select', handleSelect)
    }
  }, [emblaApi, activeIndex, normalizeIndex])

  const { onPrevButtonClick, onNextButtonClick } = usePrevNextButtons(emblaApi)

  return {
    emblaRef,
    onPrevButtonClick,
    onNextButtonClick,
  }
}
